import { useState } from "react";
import clsx from "clsx";

// Interfaces
import { IDoubleCheckingButton } from "./interfaces";

// MUI
import { ClickAwayListener, IconButton, Button, Paper, Typography, Stack } from "@mui/material";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

export default function DoubleCheckingButton(props: Readonly<IDoubleCheckingButton>) {
	const {
		title = "",
		confirmationButtonClasses = "!w-[120px] hover:!border-gray-blue hover:!shadow-gray-blue",
		confirmationButtonTextClasses = "!font-sans !text-[14.5px]",
		mainButtonClasses = "",
		mainSx = null,
		disabled = false,
		disabledOpacityUsage = false,
		onDeleteAction = () => {},
	} = props;

	const [showConfirmation, setShowConfirmation] = useState(false);

	const handleClickIn = () => {
		setShowConfirmation(true);
	};

	const handleRemove = () => {
		setShowConfirmation(false);
		onDeleteAction?.();
	};

	const handleClickAway = () => {
		setShowConfirmation(false);
	};

	return (
		<ClickAwayListener onClickAway={handleClickAway}>
			<Stack
				position={"relative"}
				sx={props.sx}
				alignItems={"center"}
				justifyContent={"center"}
				className="!transition-all !duration-200 !ease-in-out"
			>
				<Button
					variant="outlined"
					disabled={disabled}
					className={clsx({
						"!rounded-[6px] !transition-all !duration-200 !ease-in-out right-[3px]": true,
						"!font-sans !text-[14.5px] !shadow-none !font-normal !normal-case": true,
						"!h-[30px] !py-[6px] !px-[12px] !bg-gray-blue !text-white !border-gray-blue !border-[1px] !border-solid": true,
						"!absolute": !disabledOpacityUsage,
						"!opacity-100": showConfirmation && !disabledOpacityUsage,
						"!opacity-0 !invisible": !showConfirmation && !disabledOpacityUsage,
						"!hidden": !showConfirmation && disabledOpacityUsage,
						[confirmationButtonClasses]: true,
					})}
					startIcon={props.startIcon}
					endIcon={props.endIcon}
					style={{
						color: props.confirmationButtonColor,
						width: props.buttonWidth,
						top: props.processConfirmTop,
					}}
					sx={props.confirmationSx}
					onClick={handleRemove}
				>
					<Typography className={confirmationButtonTextClasses}>{title}</Typography>
				</Button>

				<IconButton
					aria-label={props.ariaLabel}
					size={props.size}
					className={clsx({
						[mainButtonClasses]: true,
						"group/double-checking-button relative": true,
						"!invisible !opacity-0": showConfirmation && !disabledOpacityUsage,
						"!opacity-100": !showConfirmation && !disabledOpacityUsage,
						"!hidden": showConfirmation && disabledOpacityUsage,
						"[&_.MuiSvgIcon-root]:!text-disabled": disabled,
					})}
					disabled={disabled}
					style={{
						color: props.mainButtonColor,
						backgroundColor: props.mainButtonBackgroundColor,
					}}
					sx={mainSx}
					onClick={handleClickIn}
				>
					<Paper
						elevation={0}
						className="!text-gray-blue !bg-transparent flex transition-all duration-200 ease-in-out group-hover/double-checking-button:opacity-0"
					>
						{props.icon || <CloseRoundedIcon />}
					</Paper>
					{props.iconHover ? (
						<Paper
							elevation={0}
							className={
								"!text-gray-blue !bg-transparent flex transition-all duration-200 ease-in-out absolute opacity-0 group-hover/double-checking-button:opacity-100"
							}
						>
							{props.iconHover}
						</Paper>
					) : (
						<Paper
							elevation={0}
							className={
								"!text-gray-blue !bg-transparent flex transition-all duration-200 ease-in-out absolute opacity-0 group-hover/double-checking-button:opacity-100"
							}
						>
							{props.icon || <CloseRoundedIcon />}
						</Paper>
					)}
				</IconButton>
			</Stack>
		</ClickAwayListener>
	);
}
